import React from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import lowerCase from 'lodash/lowerCase';
import partial from 'lodash/partial';
import startCase from 'lodash/startCase';
import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import MultiSelectEditor from './MultiSelectEditor';
import BackupAudiosSection from './BackupAudiosSection';
import AutomarkerTargetsPopupButton from './AutomarkerTargetsPopupButton';
import SectionDividers from '@dyl/components/SectionDividers';
import stubNull from '@dyl/utils/stubNull';
import AdditionalImageEditor from './AdditionalImageEditor';
import HighlightEditor from './HighlightEditor';
import PlacementEditor from './PlacementEditor';
import MessageEditor from './MessageEditor';
import DifferenceEditor from './DifferenceEditor';
import StoryEditor from "./StoryEditor";

function NotImplementedTypeComponent({ type }) {
    return (
        <Typography variant='body1' color='error'>
            Item type "{type}" is not implemented.
        </Typography>
    );
}

const ItemTypeComponents = {
    SIMPLE: stubNull,
    MULTI_SELECT: MultiSelectEditor,
    ADDITIONAL_IMAGE: AdditionalImageEditor,
    HIGHLIGHT: HighlightEditor,
    PLACEMENT: PlacementEditor,
    MESSAGE: MessageEditor,
    DIFFERENCE: DifferenceEditor,
    STORY: StoryEditor
};

const useStyles = makeStyles(theme => ({
    item: {
        marginBottom: theme.spacing(3),
    },
    divider: {
        maxWidth: theme.breakpoints.values.md,
    },
}));

export default function SpeakingGenericEditor({ disableTargets = false, includeContextAudioField = false }) {
    const classes = useStyles();

    const [items] = useTaskField('items');
    const [contextAudioId, setContextAudioId, contextAudioError] = useTaskField('context_audio_id');

    return (
        <SectionDividers DividerProps={{ className: classes.divider }}>
            { includeContextAudioField &&
                <AudioAndTranscriptBlock
                    label='Context Audio'
                    helperText='Audio to go with the context image.'
                    value={contextAudioId}
                    onChange={setContextAudioId}
                    error={contextAudioError}
                    allowEmpty={true}
                />
            }
            {items.map((item, index) => (
                <Item
                    key={index}
                    index={index}
                    className={classes.item}
                    disableTargets={disableTargets}
                />
            ))}
        </SectionDividers>
    );
}

const useItemStyles = makeStyles(theme => ({
    headerContainer: {
        marginBottom: theme.spacing(2),

        display: 'flex',
        flexDirection: 'column',

        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
        },
    },
    header: {
        marginBottom: theme.spacing(1),

        [theme.breakpoints.up('sm')]: {
            marginRight: theme.spacing(3),
            marginBottom: 0,
        },
    },
}));

function Item({ index, disableTargets = false, ...other }) {
    const classes = useItemStyles();

    const [items] = useTaskField('items');
    const useField = partial(useTaskField, 'items', index);

    const [type] = useField('item_type');
    const [isExample] = useField('is_example');
    const [audioId, setAudioId, audioError] = useField('audio_id');

    const ContentEditor = ItemTypeComponents[type];

    const exampleMapping = items.map(x => x.is_example ? 1 : 0 );
    const totalExamples = exampleMapping.reduce((acc, item) => acc + item, 0);
    const examplesUntilNow = exampleMapping.slice(0, index).reduce((acc, item) => acc + item, 0);

    return (
        <div {...other}>
            <div className={classes.headerContainer}>
                <Typography variant='h6' className={classes.header}>
                    { isExample ? `Example ${totalExamples === 1 ? '' : examplesUntilNow + 1}` : `Item ${index + 1 - examplesUntilNow}`} - {startCase(lowerCase(type)) || '???'}
                </Typography>

                {!disableTargets && !isExample && (
                    <AutomarkerTargetsPopupButton index={index} />
                )}
            </div>

            <AudioAndTranscriptBlock
                id={`task-item-${index + 1}-stimulus-audio`}
                label='Stimulus Audio'
                value={audioId}
                onChange={id => setAudioId(id)}
                error={audioError}
            />

            {(
                ContentEditor ? <ContentEditor index={index} {...other} />
                : <NotImplementedTypeComponent type={type} />
            )}

            {!isExample && <BackupAudiosSection index={index} />}
        </div>
    );
}